import { useLazyQuery, useQuery } from "@apollo/client";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../amplify/authenticator-provider";
import { GET_OPPORTUNITTIES_TYPES, GET_OPPORTUNITY_USER_TYPE } from "../graphql/query";

// TODO: currently used only by `leadOpportunityUsersForm` (the edit-users
// dialog). Still to move onto it:
//   - leadOpportunityInformationForm — has its own copies of these flags,
//     `getParentChain` and `fetchUserTypes`; the duplicate is the risk
//   - engagementActivity (reply-as role options) — queries the tab's own type
//     id, so a child type with no user types of its own returns nothing
// Until then, a change to the resolution rule has to be made in both places.

// Same two switches `leadOpportunityInformationForm` declares — user types are
// configured on the ROOT opportunity type ("Sales"), not on its children
// ("Sales / Flexible"), so querying a child's own id returns nothing.
const ENABLE_USER_TYPE_PARENT_FALLBACK = false;
const USE_ROOT_PARENT_FOR_USER_TYPE = true;

/**
 * [typeId, …ancestors] — walks `parentType` up to the root.
 *
 * `seen` guards against a cycle in the type data (A → B → A). Without it the
 * loop never terminates and the tab hangs, since nothing in the schema stops
 * an opportunity type being configured as its own ancestor.
 */
const getParentChain = (typeId, types) => {
  const chain = [typeId];
  const seen = new Set([String(typeId)]);
  const findTypeById = id => types.find(type => +type.id === +id);

  let currentId = typeId;
  while (currentId) {
    const parentId = findTypeById(currentId)?.parentType?.id;
    if (!parentId || seen.has(String(parentId))) break;
    seen.add(String(parentId));
    chain.push(parentId);
    currentId = parentId;
  }
  return chain;
};

/**
 * The opportunity user types ("Primary Assigned", "BDC", …) configured for an
 * opportunity type, resolved the same way `leadOpportunityInformationForm`
 * resolves them: against the ROOT of the type's parent chain.
 *
 * Extracted so the edit-users dialog can offer the same slots the information
 * form does. Querying a child type's own id (what the dialog used to do)
 * returns an empty list, which is why that dialog rendered blank for
 * opportunities with no users assigned yet.
 *
 * @param {Object} params
 * @param {number|string} params.opportunityTypeId the opportunity's type
 * @param {number|string} params.locationId location the types are configured on
 * @param {boolean} params.skip don't fetch
 * @returns {{userTypes: Array, loading: boolean}}
 */
const useOpportunityUserTypes = ({ opportunityTypeId, locationId, skip = false } = {}) => {
  const { user } = useContext(UserContext);
  const [userTypes, setUserTypes] = useState([]);

  // The full type tree, needed to walk from a child type up to its root.
  const { data: typesData } = useQuery(GET_OPPORTUNITTIES_TYPES, {
    variables: { industryId: 1 },
    skip,
  });
  const opportunityTypes = typesData?.getOpportunityTypes || [];

  const [getUserTypes, { loading }] = useLazyQuery(GET_OPPORTUNITY_USER_TYPE, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (skip || !opportunityTypeId || !user?.company?.id || opportunityTypes.length === 0) {
      return;
    }

    const fetch = async () => {
      const parentChain = getParentChain(opportunityTypeId, opportunityTypes);
      let idsToQuery;
      if (USE_ROOT_PARENT_FOR_USER_TYPE) {
        idsToQuery = [parentChain[parentChain.length - 1]];
      } else if (ENABLE_USER_TYPE_PARENT_FALLBACK) {
        idsToQuery = parentChain;
      } else {
        idsToQuery = [opportunityTypeId];
      }

      // Sequential on purpose: each ancestor is only tried when the one below
      // it returned nothing.
      for (let i = 0; i < idsToQuery.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await getUserTypes({
            variables: {
              locationId: +locationId || +sessionStorage.getItem("currentLocationId"),
              companyId: user.company.id,
              opportunityTypeId: +idsToQuery[i],
            },
          });
          if (result.data?.getOpportunityUserTypes?.length > 0) {
            setUserTypes(result.data.getOpportunityUserTypes);
            return;
          }
        } catch (error) {
          // Try the next ancestor when the fallback is enabled.
        }
      }
      setUserTypes([]);
    };

    fetch();
  }, [skip, opportunityTypeId, locationId, user?.company?.id, opportunityTypes.length]);

  return { userTypes, loading };
};

export default useOpportunityUserTypes;
